import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "onOpenChange", "open", "titleText", "useOnDark", "withContentPadding"];
import { Component } from 'react';
import styled from 'styled-components';
import Controllable from 'UIComponents/decorators/Controllable';
import H7 from 'UIComponents/elements/headings/H7';
import UICaret from 'UIComponents/icon/icons/UICaret';
import UIClickable from 'UIComponents/button/UIClickable';
import UIExpandableContents from 'UIComponents/accordion/UIExpandableContents';
import AccordionTransition from 'UIComponents/transitions/AccordionTransition';
import { EXPANDABLE_OPENER_COLOR, EXPANDABLE_OPENER_HOVER_COLOR } from 'UIComponents/accordion/ExpandableConstants';
import { remCalc } from 'UIComponents/core/Style';
import SyntheticEvent from 'UIComponents/core/SyntheticEvent';
import { MICRO_TRANSITION_TIMING } from 'HubStyleTokens/times';
import { BATTLESHIP, GYPSUM, KOALA } from 'HubStyleTokens/colors';

/*
Customization of https://git.hubteam.com/HubSpot/UIComponents/blob/master/UIComponents/static-3.3101/js/accordion/UIExpandableSection.js
We got permission from Canvas to create our own version of UIExpandableSection
https://git.hubteam.com/HubSpot/canvas-design/issues/292#issuecomment-1176627
Moved the caret to the right side and removed most of the overrideable props
*/
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CARET_WIDTH = 20;
const CARET_HEIGHT = 12;
const CARET_MARGIN = 12;
const ExpandableWrapper = styled.div.withConfig({
  displayName: "ExpandableSection__ExpandableWrapper",
  componentId: "sc-1cogqa9-0"
})([""]);
const ExpandableHeader = styled.div.withConfig({
  displayName: "ExpandableSection__ExpandableHeader",
  componentId: "sc-1cogqa9-1"
})(["display:flex;justify-content:space-between;padding-left:0;position:relative;height:58px;&::after{content:'';height:", ";margin-left:-1px;width:1px;}"], remCalc('54'));
const ExpandableButton = styled(UIClickable).withConfig({
  displayName: "ExpandableSection__ExpandableButton",
  componentId: "sc-1cogqa9-2"
})(["align-items:center;justify-content:space-between;display:flex;flex:1 1 auto;padding:", " 0;transition:color ", " ease-out;user-select:none;width:100%;cursor:pointer;&:hover{color:", ";}"], remCalc('12'), MICRO_TRANSITION_TIMING, EXPANDABLE_OPENER_HOVER_COLOR);
const ExpandableTitle = styled(({
  className,
  titleText
}) =>
/*#__PURE__*/
/*
 * Here, we need to null out the `aria-level` otherwise it defaults to `2`,
 * so we disable the lint rule to allow it to be a non-integer. Then we need
 * to disable the rule to allow `aria-level` to be there with `presentation`
 * since the <H7> component has a default of `role="heading"`.
 * */
// @ts-expect-error need to null out aria-level here, even though it expects a number
_jsx(H7, {
  "aria-level": null,
  className: className,
  role: "presentation",
  children: titleText
})).withConfig({
  displayName: "ExpandableSection__ExpandableTitle",
  componentId: "sc-1cogqa9-3"
})(["width:calc(100% - ", "px);"], CARET_WIDTH + CARET_MARGIN);
const ExpandableCaretHolder = styled.span.withConfig({
  displayName: "ExpandableSection__ExpandableCaretHolder",
  componentId: "sc-1cogqa9-4"
})(["display:flex;flex-shrink:0;justify-content:center;"]);
const ExpandableCaret = styled(UICaret).attrs({
  color: EXPANDABLE_OPENER_COLOR,
  height: CARET_HEIGHT,
  width: CARET_WIDTH
}).withConfig({
  displayName: "ExpandableSection__ExpandableCaret",
  componentId: "sc-1cogqa9-5"
})(["margin-left:", "px;transition:all ", " ease-in-out;transform:", ";"], CARET_MARGIN, MICRO_TRANSITION_TIMING, props => props.open ? 'rotate(270deg)' : 'rotate(90deg)');
const ExpandableContents = styled(UIExpandableContents).withConfig({
  displayName: "ExpandableSection__ExpandableContents",
  componentId: "sc-1cogqa9-6"
})(["background-color:", ";padding-left:", ";padding-right:", ";box-sizing:border-box;border-top:", ";"], props => props.useOnDark ? KOALA : GYPSUM, props => props.withPadding ? '24px' : '0', props => props.withPadding ? '24px' : '0', props => props.open ? `1px solid ${BATTLESHIP}` : 'none');
class ExpandableSection extends Component {
  constructor(props) {
    super(props);
    this.displayName = 'ExpandableSection';
    this.onClick = this.onClick.bind(this);
  }
  onClick() {
    const {
      onOpenChange,
      open
    } = this.props;
    onOpenChange(SyntheticEvent(!open));
  }
  render() {
    const _this$props = this.props,
      {
        children,
        open,
        titleText,
        useOnDark,
        withContentPadding
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const extraProps = {
      open
    };
    return /*#__PURE__*/_jsxs(ExpandableWrapper, Object.assign({}, rest, extraProps, {
      children: [/*#__PURE__*/_jsx(ExpandableHeader, Object.assign({
        className: "expandable-section-header"
      }, extraProps, {
        children: titleText ? /*#__PURE__*/_jsxs(ExpandableButton, {
          "aria-expanded": open ? 'true' : 'false',
          onClick: this.onClick,
          children: [/*#__PURE__*/_jsx(ExpandableTitle, {
            className: "m-y-0",
            titleText: titleText
          }), /*#__PURE__*/_jsx(ExpandableCaretHolder, {
            children: /*#__PURE__*/_jsx(ExpandableCaret, {
              open: open
            })
          })]
        }) : null
      })), children ? /*#__PURE__*/_jsx(AccordionTransition, {
        open: open,
        children: /*#__PURE__*/_jsx(ExpandableContents, {
          "aria-hidden": open ? 'false' : 'true',
          className: "expandable-section-contents",
          indentation: "none",
          open: open,
          useOnDark: useOnDark,
          withPadding: withContentPadding,
          children: children
        })
      }) : null]
    }));
  }
}
ExpandableSection.defaultProps = {
  open: false
};
export default Controllable(ExpandableSection, ['open']);